import { useId, useMemo, useRef, useState } from 'react';
import { CALENDAR_DIALOG_LABEL, CalendarPopover } from './CalendarPopover';
import { CalendarIcon } from './CalendarIcon';
import { Tooltip } from './Tooltip';
import { fieldClass } from './fieldClass';
import { cn } from '../../lib/utils';
import { formatDayInput, parseDate } from '@packages/format';

const INVALID_DATE_MESSAGE = 'Use dd/mm/yyyy within the allowed range.';
const FIRST_OF_MONTH_MESSAGE = 'Use the first day of a month.';
const LAST_OF_MONTH_MESSAGE = 'Use the last day of a month.';

export interface CalendarPopoverButtonProps {
  label: string;
  /**
   * Optional id for the trigger button. Set this when an external `<label>`
   * (e.g. one rendering its own AI badge) associates to the control via
   * `htmlFor`; pass `label=""` in that case so the internal label is skipped.
   */
  id?: string;
  value: string | null;
  placeholder?: string;
  onChange: (iso: string) => void;
  onClear?: () => void;
  allowClear?: boolean;
  errorState?: boolean;
  /** Show the valid typed-date indicator. Defaults to true. */
  showValidIndicator?: boolean;
  /**
   * Id of an external error message (e.g. a `FieldError` the caller renders
   * itself) to merge into `aria-describedby` alongside the in-component
   * invalid-parse message. Needed because `errorState` alone leaves
   * `aria-describedby` unset - see the accessibility carve-out note in
   * `apps/web/bill/CLAUDE.md`.
   */
  describedById?: string;
  /** When true, the trigger button is disabled and the popover cannot open. */
  disabled?: boolean;
  /** Tailwind width class applied to the trigger button, e.g. 'w-44' or 'w-full'. */
  widthClass?: string;
  /** Earliest selectable date in YYYY-MM-DD format (inclusive). Forwarded to CalendarPopover. */
  minDate?: string;
  /** Latest selectable date in YYYY-MM-DD format (inclusive). Forwarded to CalendarPopover. */
  maxDate?: string;
  /**
   * When true, only the 1st of each month is selectable. Forwarded to CalendarPopover.
   * Useful for the ledger opening-date picker where the 1st-of-month constraint is
   * enforced server-side.
   */
  disableNonFirstOfMonth?: boolean;
  /** When true, only the last day of each month is selectable. */
  disableNonLastOfMonth?: boolean;
  /**
   * When true, the popover opens immediately on mount. Useful when a parent
   * transitions to this component programmatically (e.g. DateFilterInput chip
   * "Change" click) and expects the popover already open.
   * Defaults to false.
   */
  initialOpen?: boolean;
}

/**
 * A self-contained date picker: a styled trigger input that opens a
 * CalendarPopover anchored to itself. Manages its own open/closed state
 * and the anchor ref internally so call sites don't need to.
 *
 * Integrates with react-hook-form Controller via `onChange`/`value` props.
 */
export function CalendarPopoverButton({
  label,
  id,
  value,
  placeholder = 'dd/mm/yyyy',
  onChange,
  onClear,
  allowClear = false,
  errorState = false,
  showValidIndicator = true,
  describedById,
  disabled = false,
  widthClass = 'w-full',
  minDate,
  maxDate,
  disableNonFirstOfMonth = false,
  disableNonLastOfMonth = false,
  initialOpen = false,
}: CalendarPopoverButtonProps) {
  const anchorRef = useRef<HTMLInputElement>(null);
  const fieldWrapperRef = useRef<HTMLDivElement>(null);
  const ignoreNextFocusRef = useRef(false);
  // Set on a Tab keydown, consumed by the very next blur. A Tab keypress is
  // the operator leaving the field, whatever the browser focuses next - but
  // committing directly on the keydown is unsafe: committing calls onChange,
  // which on every DateFilterInput-style consumer flips isEditing to false
  // and unmounts this input before Tab's native focus-move runs, stranding
  // focus on <body> instead of the next control. So the keydown only records
  // "the operator left by Tab" and the blur that follows does the commit
  // once the browser has actually decided where focus is going. The ref
  // (rather than inferring "left by keyboard" from where focus landed) is
  // what lets the blur handler commit even when Tab lands inside the
  // calendar panel - see onKeyDown and onBlur below for why that happens.
  const leftByTabRef = useRef(false);
  const generatedId = useId();
  const inputId = id ?? `${generatedId}-date-input`;
  const [open, setOpen] = useState(initialOpen);
  const [lastExternalValue, setLastExternalValue] = useState(value);
  const [inputValue, setInputValue] = useState(() => (value ? formatDayInput(value) : ''));
  // The iso this field last committed (via onChange), so a leave-the-field
  // commit path can tell "I already sent this" from "this is new" without
  // depending on the `value` prop having caught up. Two functions call
  // onChange for a departure - commitTypedDate (Enter) and
  // commitTypedDateOnBlur (everything else) - and Enter's deferred focus
  // move can trigger a blur that reaches the second one right after the
  // first already sent the same value; comparing against this ref (updated
  // synchronously by both) makes that second call a no-op without waiting
  // on a re-render. Synced to `value` whenever it changes externally (see
  // below), so a later retype of a date this ref still holds is correctly
  // treated as already-committed, while a retype after the external value
  // has moved on (a calendar pick, a URL change) commits again.
  const lastCommittedIsoRef = useRef<string | null>(value);

  const { parsedInput, invalidDateMessage } = useMemo(() => {
    const parsed = parseDate(inputValue);
    if (parsed.state === 'valid' && parsed.iso !== null) {
      const outsideAllowedRange =
        (minDate !== undefined && parsed.iso < minDate) ||
        (maxDate !== undefined && parsed.iso > maxDate);
      if (outsideAllowedRange) {
        return {
          parsedInput: { state: 'invalid' as const, iso: null },
          invalidDateMessage: INVALID_DATE_MESSAGE,
        };
      }
      if (disableNonFirstOfMonth && !parsed.iso.endsWith('-01')) {
        return {
          parsedInput: { state: 'invalid' as const, iso: null },
          invalidDateMessage: FIRST_OF_MONTH_MESSAGE,
        };
      }
      if (disableNonLastOfMonth) {
        const [year, month, day] = parsed.iso.split('-').map(Number);
        const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
        if (day !== lastDay) {
          return {
            parsedInput: { state: 'invalid' as const, iso: null },
            invalidDateMessage: LAST_OF_MONTH_MESSAGE,
          };
        }
      }
    }
    return { parsedInput: parsed, invalidDateMessage: INVALID_DATE_MESSAGE };
  }, [inputValue, minDate, maxDate, disableNonFirstOfMonth, disableNonLastOfMonth]);
  const showValidStatus = showValidIndicator && parsedInput.state === 'valid';
  const showInvalidStatus = parsedInput.state === 'invalid';
  const errorMessageId = `${inputId}-error`;

  if (value !== lastExternalValue) {
    setLastExternalValue(value);
    setInputValue(value ? formatDayInput(value) : '');
    lastCommittedIsoRef.current = value;
  }

  function focusDayGrid() {
    requestAnimationFrame(() => {
      document
        .querySelector<HTMLButtonElement>('[role="dialog"] [role="grid"] button[tabindex="0"]')
        ?.focus();
    });
  }

  function openKeepingInputFocus() {
    if (ignoreNextFocusRef.current) {
      ignoreNextFocusRef.current = false;
      return;
    }
    setOpen(true);
  }

  function closePopover() {
    setOpen(false);
  }

  // Passed to CalendarPopover as onReturnFocus: called only when the popover
  // returns focus to the input on close (focus was inside the calendar). Sets
  // the suppress flag immediately before the focus so the resulting onFocus
  // does not reopen the popover. Closes that close focus elsewhere (Tab to the
  // next field, outside click) never call this, so the flag cannot go stale.
  function returnFocusToInput() {
    ignoreNextFocusRef.current = true;
    anchorRef.current?.focus();
  }

  // Move focus to the next focusable control in the same form. Used after an
  // Enter commit so keyboard users advance to the next field (e.g. invoice
  // date -> due date) instead of staying on the just-filled input. No-op when
  // the field is not inside a form (e.g. a filter bar), so those keep the
  // commit-and-stay behaviour.
  function focusNextFieldInForm() {
    const input = anchorRef.current;
    const form = input?.form;
    if (!input || !form) return;
    const focusable = Array.from(
      form.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    );
    const index = focusable.indexOf(input);
    if (index === -1) return;
    // Focusing a hidden element is a no-op, so a hidden next control simply
    // leaves focus on the input rather than advancing - acceptable for the
    // date-field-then-field forms this serves.
    focusable[index + 1]?.focus();
  }

  // Unlike commitTypedDateOnBlur, this fires onChange even when the typed
  // text is unchanged from what was last committed. Enter is an explicit
  // confirm - the operator asking for it is not the same as tabbing through
  // an untouched field - so the asymmetry with the blur path is deliberate,
  // not an oversight.
  function commitTypedDate() {
    if (parsedInput.state !== 'valid' || parsedInput.iso === null) return;
    lastCommittedIsoRef.current = parsedInput.iso;
    onChange(parsedInput.iso);
    setInputValue(formatDayInput(parsedInput.iso));
    closePopover();
    requestAnimationFrame(() => focusNextFieldInForm());
  }

  // Commit a valid typed date when focus genuinely leaves the field, without
  // the two things an Enter commit also does: closing the popover (the blur
  // handler that calls this already owns that decision) and moving focus to
  // the next field (the operator has already chosen where focus goes by
  // tabbing or clicking, and yanking it elsewhere would fight that choice).
  // Also, unlike commitTypedDate, this notifies the parent only when the
  // typed text differs from what this field last committed - some onChange
  // consumers write straight to the backend or the URL, and an operator who
  // tabs through an untouched, pre-filled field has not asked for either.
  //
  // The equality check against lastCommittedIsoRef also makes this safe to
  // call a second time for the same departure: commitTypedDate (the Enter
  // path) moves focus to the next field via a deferred requestAnimationFrame
  // call, and that focus move blurs this input, running this function again
  // for the value commitTypedDate already sent. Comparing against the ref (a
  // ref, not the `value` prop, and updated synchronously by both commit
  // functions) makes that second call a no-op without depending on the
  // caller having re-rendered with the new value yet.
  function commitTypedDateOnBlur() {
    if (parsedInput.state !== 'valid' || parsedInput.iso === null) return;
    if (parsedInput.iso === lastCommittedIsoRef.current) return;
    lastCommittedIsoRef.current = parsedInput.iso;
    onChange(parsedInput.iso);
    setInputValue(formatDayInput(parsedInput.iso));
  }

  // Clear the typed entry and return focus to the input so the user can retype.
  // Touches only the in-progress text, not the committed value.
  function clearTypedDate() {
    setInputValue('');
    setOpen(true);
    requestAnimationFrame(() => anchorRef.current?.focus());
  }

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={inputId} className="text-sm font-medium text-secondary">
          {label}
        </label>
      )}
      <div ref={fieldWrapperRef} className={`relative ${widthClass}`}>
        <CalendarIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
        <input
          ref={anchorRef}
          id={inputId}
          type="text"
          inputMode="numeric"
          disabled={disabled}
          value={inputValue}
          placeholder={placeholder}
          aria-invalid={showInvalidStatus || errorState}
          aria-describedby={[showInvalidStatus ? errorMessageId : undefined, describedById].filter(Boolean).join(' ') || undefined}
          autoFocus={initialOpen}
          onFocus={openKeepingInputFocus}
          onClick={openKeepingInputFocus}
          onChange={(e) => {
            setInputValue(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitTypedDate();
              return;
            }
            // A Tab keypress out of the input IS the operator leaving the
            // field, whatever the browser focuses next - so it must commit.
            // It cannot commit here on the key though: committing calls
            // onChange, which on every DateFilterInput-style consumer flips
            // isEditing to false and unmounts this input, and React flushes
            // that update synchronously inside this handler - before the
            // browser performs Tab's native focus move. The browser then
            // starts sequential focus navigation from a detached node and
            // focus falls back to <body> instead of the next control. So
            // this only records that the departure was a Tab; the blur that
            // follows (once the browser has actually decided where focus is
            // going) does the commit. Tab keeps its native focus-moving
            // behaviour (no preventDefault).
            if (e.key === 'Tab') {
              leftByTabRef.current = true;
              return;
            }
            // ArrowDown moves into the calendar grid.
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              setOpen(true);
              focusDayGrid();
            }
          }}
          onBlur={(e) => {
            // Consumed once per blur and always cleared, so a Tab that
            // somehow produces no blur (there is currently no such path)
            // cannot leave this set for a later, unrelated departure.
            const committedByTab = leftByTabRef.current;
            leftByTabRef.current = false;

            const next = e.relatedTarget as Element | null;
            // Focus staying inside the field's own controls (the clear
            // button) is not a departure - no commit unless a Tab produced
            // this blur, no close either way.
            if (next !== null && fieldWrapperRef.current?.contains(next)) {
              if (committedByTab) commitTypedDateOnBlur();
              return;
            }
            // Match this calendar rather than any dialog: fields inside a modal
            // still close on departure. Tab into the portal commits; explicit
            // ArrowDown or a click into the calendar keeps the typed draft.
            if (next !== null && next.closest(`[role="dialog"][aria-label="${CALENDAR_DIALOG_LABEL}"]`)) {
              if (committedByTab) commitTypedDateOnBlur();
              return;
            }
            // A null relatedTarget can mean a genuine in-page departure -
            // Safari does not focus a button on click, so an ordinary
            // click-away has no relatedTarget there - or the window/tab
            // losing focus entirely, which browsers disagree on whether it
            // also blurs the active element with a null relatedTarget. Gate
            // on document.hasFocus() so only the former commits: an operator
            // who types a date and switches away to check something before
            // confirming has not asked for it to be applied.
            if (next === null) {
              if (document.hasFocus()) commitTypedDateOnBlur();
              return;
            }
            // Focus has genuinely left the field for a real element outside
            // it - Tab to the next field, or a click elsewhere. A typed date
            // the operator never confirmed with Enter or a day-click would
            // otherwise sit visibly in the field while the form still held
            // the old value, so commit it here too.
            commitTypedDateOnBlur();
            setOpen(false);
          }}
          className={cn(fieldClass({ error: errorState }), 'h-[38px] w-full cursor-pointer py-2 pl-9 pr-9 text-secondary transition-colors placeholder:text-text-muted')}
        />
        {showValidStatus && (
          <svg
            className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-success"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path d="M4 10.5l4 4L16 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
        {showInvalidStatus && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2">
            <Tooltip content={invalidDateMessage}>
              <button
                type="button"
                aria-label="Clear date"
                onClick={clearTypedDate}
                className="flex h-6 w-6 items-center justify-center rounded text-error transition-colors hover:bg-error/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-error/30"
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path d="M5 5l10 10M15 5L5 15" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </Tooltip>
          </span>
        )}
      </div>
      {showInvalidStatus && (
        <span id={errorMessageId} role="alert" className="sr-only">
          {invalidDateMessage}
        </span>
      )}
      {open && (
        <CalendarPopover
          anchorRef={anchorRef}
          autoFocus={false}
          dismissExcludeRef={fieldWrapperRef}
          onReturnFocus={returnFocusToInput}
          selectedDate={value}
          navigateToDate={parsedInput.state === 'valid' ? parsedInput.iso : null}
          onPick={(iso) => {
            lastCommittedIsoRef.current = iso;
            onChange(iso);
            setInputValue(formatDayInput(iso));
            closePopover();
          }}
          allowClear={allowClear}
          onClear={
            onClear
              ? () => {
                  lastCommittedIsoRef.current = null;
                  onClear();
                  setInputValue('');
                  closePopover();
                }
              : undefined
          }
          onClose={closePopover}
          minDate={minDate}
          maxDate={maxDate}
          disableNonFirstOfMonth={disableNonFirstOfMonth}
          disableNonLastOfMonth={disableNonLastOfMonth}
        />
      )}
    </div>
  );
}
